import { normalizeApiUrl } from "@medicorepay/shared/lib/api-client";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatBusinessDate } from "../../lib/utils";

interface ExpiryReportFilters {
  period: "expired" | "30_days" | "60_days" | "90_days" | "ALL";
  storeId?: string;
  categoryId?: string;
}

interface ExpiryReportSummary {
  expiredBatches: number;
  expiredValue: number;
  expiringSoon30Days: number;
  expiringSoon60Days: number;
  expiringSoon90Days: number;
  totalValueAtRisk: number;
  affectedProducts: number;
}

interface BatchExpiryDetail {
  batchId: string;
  batchNumber: string;
  productId: string;
  productName: string;
  sku: string;
  categoryName?: string;
  quantity: number;
  expiryDate: string;
  daysUntilExpiry: number;
  costPrice: number;
  totalValue: number;
  status: "EXPIRED" | "EXPIRING_SOON" | "WARNING";
  riskLevel: "HIGH" | "MEDIUM" | "LOW";
}

interface CategoryExpiryBreakdown {
  categoryId: string;
  categoryName: string;
  batchesCount: number;
  expiredCount: number;
  expiringSoonCount: number;
  totalValue: number;
  totalQuantity: number;
}

interface ExpiryReportData {
  summary: ExpiryReportSummary;
  batches: BatchExpiryDetail[];
  categoryBreakdown: CategoryExpiryBreakdown[];
  total: number;
}

const REPORTS_API_URL = normalizeApiUrl(
  import.meta.env.VITE_API_URL || "http://localhost:5001",
);

const COLORS = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#10B981",
  "#3B82F6",
  "#8B5CF6",
];

const getStatusColor = (status: string) => {
  switch (status) {
    case "EXPIRED":
      return "bg-red-100 text-red-800";
    case "EXPIRING_SOON":
      return "bg-orange-100 text-orange-800";
    case "WARNING":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getRiskColor = (risk: string) => {
  switch (risk) {
    case "HIGH":
      return "bg-red-600 text-white";
    case "MEDIUM":
      return "bg-orange-500 text-white";
    case "LOW":
      return "bg-yellow-500 text-white";
    default:
      return "bg-gray-500 text-white";
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "EXPIRED":
      return "Expired";
    case "EXPIRING_SOON":
      return "Expiring Soon";
    case "WARNING":
      return "Warning";
    default:
      return status;
  }
};

export default function ExpiryReportPage() {
  const [filters, setFilters] = useState<ExpiryReportFilters>({
    period: "ALL",
  });

  const [reportData, setReportData] = useState<ExpiryReportData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        ...(filters.period !== "ALL" && { period: filters.period }),
        ...(filters.storeId && { storeId: filters.storeId }),
        ...(filters.categoryId && { categoryId: filters.categoryId }),
      });

      const response = await fetch(
        `${REPORTS_API_URL}/reports/expiry?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      }
    } catch (error) {
      console.error("Failed to fetch expiry report:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters.period !== "ALL" && { period: filters.period }),
        ...(filters.storeId && { storeId: filters.storeId }),
        ...(filters.categoryId && { categoryId: filters.categoryId }),
      });

      const response = await fetch(
        `${REPORTS_API_URL}/reports/expiry/export?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `expiry-report-${filters.period}-${formatBusinessDate(new Date())}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Failed to export report:", error);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Expiry Report</h1>
        <p className="text-gray-600">
          Track expiring and expired batches for FEFO compliance
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiry Period
            </label>
            <select
              value={filters.period}
              onChange={(e) =>
                setFilters({ ...filters, period: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Batches</option>
              <option value="expired">Expired</option>
              <option value="30_days">Expiring in 30 Days</option>
              <option value="60_days">Expiring in 60 Days</option>
              <option value="90_days">Expiring in 90 Days</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? "Loading..." : "Generate Report"}
            </button>
          </div>
          <div className="flex items-end">
            <button
              onClick={exportToExcel}
              disabled={!reportData}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {reportData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Batches
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {reportData.summary.expiredBatches +
                      reportData.summary.expiringSoon30Days +
                      reportData.summary.expiringSoon60Days +
                      reportData.summary.expiringSoon90Days}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Expired Batches
                  </p>
                  <p className="text-3xl font-bold text-red-600">
                    {reportData.summary.expiredBatches}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ₹{reportData.summary.expiredValue.toFixed(2)} value lost
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Expiring Soon
                  </p>
                  <p className="text-3xl font-bold text-orange-600">
                    {reportData.summary.expiringSoon30Days +
                      reportData.summary.expiringSoon60Days +
                      reportData.summary.expiringSoon90Days}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ₹
                    {(
                      reportData.summary.totalValueAtRisk -
                      reportData.summary.expiredValue
                    ).toFixed(2)}{" "}
                    at risk
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Value at Risk
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    ₹{reportData.summary.totalValueAtRisk.toFixed(2)}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Category Breakdown */}
            {reportData.categoryBreakdown &&
              reportData.categoryBreakdown.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">
                    Expiry Risk by Category
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={reportData.categoryBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="categoryName" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="expiredCount"
                        fill="#EF4444"
                        name="Expired"
                        stackId="a"
                      />
                      <Bar
                        dataKey="expiringCount"
                        fill="#F97316"
                        name="Expiring"
                        stackId="a"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

            {/* Value at Risk Distribution */}
            {reportData.categoryBreakdown &&
              reportData.categoryBreakdown.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">
                    Value at Risk by Category
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={reportData.categoryBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) =>
                          `${entry.categoryName}: ₹${entry.totalValue.toFixed(0)}`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="totalValue"
                      >
                        {reportData.categoryBreakdown.map((_item, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
          </div>

          {/* Batch Details Table */}
          {reportData.batches && reportData.batches.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow mb-6">
              <h3 className="text-lg font-semibold mb-4">
                Batch Expiry Details
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Batch #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Days Left
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Batch Value
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Risk
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.batches.map((batch, index) => (
                      <tr
                        key={batch.batchId}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {batch.batchNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {batch.productName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {batch.sku}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {batch.categoryName || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {batch.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {new Date(batch.expiryDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <span
                            className={
                              batch.daysUntilExpiry < 0
                                ? "text-red-600 font-semibold"
                                : "text-gray-900"
                            }
                          >
                            {batch.daysUntilExpiry < 0
                              ? `${Math.abs(batch.daysUntilExpiry)} days ago`
                              : `${batch.daysUntilExpiry} days`}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          ₹{batch.totalValue.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}
                          >
                            {getStatusBadge(batch.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`px-2 py-1 rounded text-xs font-bold ${getRiskColor(batch.riskLevel)}`}
                          >
                            {batch.riskLevel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Category Breakdown Table */}
          {reportData.categoryBreakdown &&
            reportData.categoryBreakdown.length > 0 && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">
                  Category Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Batches
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expired
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expiring
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Value at Risk
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Risk %
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.categoryBreakdown.map((category, index) => {
                        const riskPercentage =
                          ((category.expiredCount +
                            category.expiringSoonCount) /
                            category.batchesCount) *
                          100;
                        return (
                          <tr
                            key={category.categoryId}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {category.categoryName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {category.batchesCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-right font-semibold">
                              {category.expiredCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 text-right font-semibold">
                              {category.expiringSoonCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              ₹{category.totalValue.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              <div className="flex items-center justify-end">
                                <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                                  <div
                                    className={`h-2 rounded-full ${riskPercentage > 50 ? "bg-red-600" : riskPercentage > 25 ? "bg-orange-500" : "bg-yellow-500"}`}
                                    style={{
                                      width: `${Math.min(riskPercentage, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                                <span className="text-gray-900">
                                  {riskPercentage.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </>
      )}

      {/* Empty State */}
      {!reportData && !loading && (
        <div className="bg-white p-12 rounded-lg shadow text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No report data
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Select expiry period and filters, then click "Generate Report" to
            view expiry analytics.
          </p>
        </div>
      )}
    </div>
  );
}
